import React, { useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { CheckCircle, XCircle, Clock, RefreshCw, AlertCircle, Loader2, Eye, Phone } from 'lucide-react';
import orderService from '../../services/orderService';
import ManualRefunds from './ManualRefunds';

/**
 * The last tab is not a refund-request status. It lists a different record
 * entirely — money owed on doorstep collections that no gateway will return —
 * and lives here because this screen is the admin's refund desk.
 */
const MANUAL_TAB = 'manual';

const STATUS_TABS = [
  { label: 'Pending', value: 'pending' },
  { label: 'Approved', value: 'approved' },
  { label: 'Rejected', value: 'rejected' },
  { label: 'All', value: '' },
  { label: 'Manual Refunds', value: MANUAL_TAB },
];

const formatCurrency = (amount) =>
  `₹${Number(amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;

const formatDate = (dateStr) =>
  new Date(dateStr).toLocaleString('en-IN', {
    day: 'numeric', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Kolkata',
  });

function ReasonPopover({ text, label = 'Reason' }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  if (!text) return <span className="text-gray-300 text-xs">—</span>;

  return (
    <div className="relative inline-block" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1 text-xs text-[#548B2E] hover:text-[#3a6020] font-medium transition-colors"
        title={`View ${label}`}
      >
        <Eye className="w-3.5 h-3.5" />
        View
      </button>
      {open && (
        <div className="absolute left-0 top-full mt-1.5 z-50 w-64 bg-white border border-gray-200 rounded-xl shadow-xl p-3">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">{label}</p>
          <p className="text-sm text-gray-800 leading-relaxed whitespace-pre-wrap">{text}</p>
        </div>
      )}
    </div>
  );
}

ReasonPopover.propTypes = { text: PropTypes.string, label: PropTypes.string };

function RejectModal({ onConfirm, onClose, isLoading }) {
  const [reason, setReason] = useState('');
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <button type="button" aria-label="Close" className="absolute inset-0 w-full h-full bg-black/50 backdrop-blur-sm cursor-default" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 space-y-4">
        <h3 className="text-lg font-bold text-gray-900">Reject Refund Request</h3>
        <p className="text-sm text-gray-600">Please provide a reason for rejection. This will be visible to the user.</p>
        <textarea
          className="w-full border border-gray-300 rounded-xl p-3 text-sm focus:outline-none focus:ring-2 focus:ring-red-400 resize-none"
          rows={4}
          placeholder="Enter rejection reason…"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />
        <div className="flex gap-3">
          <button
            onClick={onClose}
            disabled={isLoading}
            className="flex-1 px-4 py-2.5 border border-gray-200 text-gray-700 rounded-xl font-medium hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={() => reason.trim() && onConfirm(reason.trim())}
            disabled={isLoading || !reason.trim()}
            className="flex-1 px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-xl font-semibold disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <XCircle className="w-4 h-4" />}
            Reject
          </button>
        </div>
      </div>
    </div>
  );
}

RejectModal.propTypes = { onConfirm: PropTypes.func, onClose: PropTypes.func, isLoading: PropTypes.bool };

export default function RefundRequests({ newCount = 0 }) {
  const [requests, setRequests] = useState([]);
  const [highlightActive, setHighlightActive] = useState(false);

  useEffect(() => {
    if (newCount > 0) {
      setHighlightActive(true);
      const t = setTimeout(() => setHighlightActive(false), 3000);
      return () => clearTimeout(t);
    }
  }, [newCount]);
  const [activeTab, setActiveTab] = useState('pending');
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [rejectModal, setRejectModal] = useState(null);
  const [toast, setToast] = useState(null);

  // Memoised because ManualRefunds takes it as a prop and depends on it inside
  // its own fetch callback — a fresh function each render would re-trigger that
  // effect forever.
  const showToast = useCallback((message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  }, []);

  const fetchRequests = useCallback(async () => {
    // The manual-refunds tab loads its own data from a different endpoint.
    if (activeTab === MANUAL_TAB) return;
    setLoading(true);
    const result = await orderService.getRefundRequests(activeTab);
    if (result.success) setRequests(result.data);
    else showToast(result.error || 'Failed to load refund requests', 'error');
    setLoading(false);
  }, [activeTab, showToast]);

  useEffect(() => { fetchRequests(); }, [fetchRequests]);

  const handleApprove = async (id) => {
    setActionLoading(id + '_approve');
    const result = await orderService.approveRefundRequest(id);
    setActionLoading(null);
    if (result.success) {
      // A COD order collected at the door has no gateway payment to reverse, so
      // approving it creates an obligation a person must settle by hand. Saying
      // "Razorpay refund initiated" there told the admin something was already
      // in motion when nothing was.
      const isManual = result.data?.refund?.payment?.paymentMethod === 'cod';
      showToast(isManual
        ? 'Refund approved — added to Manual Refunds to be sent by hand'
        : 'Refund approved — Razorpay refund initiated');
      fetchRequests();
    } else {
      showToast(result.error || 'Approval failed', 'error');
    }
  };

  const handleReject = async (reason) => {
    const id = rejectModal;
    setRejectModal(null);
    setActionLoading(id + '_reject');
    const result = await orderService.rejectRefundRequest(id, reason);
    setActionLoading(null);
    if (result.success) {
      showToast('Refund request rejected');
      fetchRequests();
    } else {
      showToast(result.error || 'Rejection failed', 'error');
    }
  };

  const statusBadge = (status) => {
    if (status === 'pending') return <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700"><Clock className="w-3 h-3" />Pending</span>;
    if (status === 'approved') return <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700"><CheckCircle className="w-3 h-3" />Approved</span>;
    return <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700"><XCircle className="w-3 h-3" />Rejected</span>;
  };

  return (
    <div className="space-y-6">
      {/* Toast */}
      {toast && (
        <div className={`fixed top-4 right-4 z-50 flex items-center gap-2 px-4 py-3 rounded-xl shadow-lg text-sm font-medium ${
          toast.type === 'error' ? 'bg-red-50 text-red-800 border border-red-200' : 'bg-green-50 text-green-800 border border-green-200'
        }`}>
          {toast.type === 'error' ? <AlertCircle className="w-4 h-4" /> : <CheckCircle className="w-4 h-4" />}
          {toast.message}
        </div>
      )}

      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Refund Requests</h2>
          <p className="text-sm text-gray-500 mt-0.5">
            {activeTab === MANUAL_TAB
              ? 'Track refunds that have to be returned by hand'
              : 'Review and process source refund requests from cancelled orders'}
          </p>
        </div>
        {/* Manual Refunds reloads itself when its Owed/Sent toggle changes. */}
        {activeTab !== MANUAL_TAB && (
          <button
            onClick={fetchRequests}
            className="flex items-center gap-2 px-4 py-2 text-sm border border-gray-200 rounded-xl hover:bg-gray-50 text-gray-600"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 p-1 bg-gray-100 rounded-xl w-fit">
        {STATUS_TABS.map((tab) => (
          <button
            key={tab.value}
            onClick={() => setActiveTab(tab.value)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              activeTab === tab.value
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Table */}
      {activeTab === MANUAL_TAB ? <ManualRefunds showToast={showToast} /> : (() => {
        if (loading) return (
          <div className="flex items-center justify-center py-16 text-gray-400">
            <Loader2 className="w-6 h-6 animate-spin mr-2" />
            Loading…
          </div>
        );
        if (requests.length === 0) return (
          <div className="flex flex-col items-center justify-center py-16 text-gray-400">
            <CheckCircle className="w-10 h-10 mb-3" />
            <p className="text-sm font-medium">No refund requests found</p>
          </div>
        );
        return (
        <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Req. ID</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Order</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Customer</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Amount</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Requested</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Cancel Reason</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Status</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Reviewed At</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Reject Reason</th>
                  <th className="text-right px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {requests.map((req, index) => (
                  <tr key={req.id} className={`hover:bg-gray-50 transition-colors ${highlightActive && index < newCount ? 'bg-orange-50 border-l-4 border-orange-400' : ''}`}>
                    <td className="px-5 py-4 text-gray-400 font-mono text-xs">#{req.id}</td>

                    {/* Order — show order number prominently, internal id as sub-label */}
                    <td className="px-5 py-4">
                      <p className="font-semibold text-gray-900 text-sm">
                        {req.orderNumber || `ORD-${req.orderId}`}
                      </p>
                      <p className="text-xs text-gray-400 mt-0.5">ID #{req.orderId}</p>
                    </td>

                    {/* Customer — name + mobile number */}
                    <td className="px-5 py-4">
                      {req.customerName || req.customerPhone ? (
                        <>
                          <p className="font-medium text-gray-800 text-sm">{req.customerName || '—'}</p>
                          {req.customerPhone && (
                            <p className="text-xs text-gray-500 mt-0.5 flex items-center gap-1">
                              <Phone className="w-3 h-3" /> {req.customerPhone}
                            </p>
                          )}
                        </>
                      ) : (
                        <span className="text-gray-300 text-xs">—</span>
                      )}
                    </td>

                    <td className="px-5 py-4 font-semibold text-gray-900">{formatCurrency(req.amount)}</td>

                    <td className="px-5 py-4 text-gray-500 text-xs">{formatDate(req.requestedAt)}</td>

                    {/* Cancel Reason — eye icon popover */}
                    <td className="px-5 py-4">
                      <ReasonPopover text={req.cancellationReason} label="Cancellation Reason" />
                    </td>

                    <td className="px-5 py-4">{statusBadge(req.status)}</td>

                    <td className="px-5 py-4 text-gray-500 text-xs">
                      {req.reviewedAt ? formatDate(req.reviewedAt) : '—'}
                    </td>

                    {/* Reject Reason — eye icon popover */}
                    <td className="px-5 py-4">
                      <ReasonPopover text={req.rejectionReason} label="Rejection Reason" />
                    </td>

                    <td className="px-5 py-4">
                      {req.status === 'pending' && (
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => handleApprove(req.id)}
                            disabled={!!actionLoading}
                            className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white text-xs font-semibold rounded-lg disabled:opacity-50 transition-colors"
                          >
                            {actionLoading === req.id + '_approve'
                              ? <Loader2 className="w-3 h-3 animate-spin" />
                              : <CheckCircle className="w-3 h-3" />}
                            Approve
                          </button>
                          <button
                            onClick={() => setRejectModal(req.id)}
                            disabled={!!actionLoading}
                            className="flex items-center gap-1.5 px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white text-xs font-semibold rounded-lg disabled:opacity-50 transition-colors"
                          >
                            {actionLoading === req.id + '_reject'
                              ? <Loader2 className="w-3 h-3 animate-spin" />
                              : <XCircle className="w-3 h-3" />}
                            Reject
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        );
      })()}

      {rejectModal && (
        <RejectModal
          onConfirm={handleReject}
          onClose={() => setRejectModal(null)}
          isLoading={!!actionLoading}
        />
      )}
    </div>
  );
}

RefundRequests.propTypes = { newCount: PropTypes.number };
